// components/ProductDetails.js
import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./ProductDetails.css";
import PageHeader from "../components/PageHeader";
import Footer from "../components/Footer";
import sellerProductData from "./SellerSegment";
import EditRejectedProductModal from "../components/EditRejectedProductModal";
import { AlertCircle, Edit3 } from "lucide-react";

const ProductDetails = () => {
  // Navigation & location
  const navigate = useNavigate();
  const location = useLocation();
  const product = location.state?.product;

  // State
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedLotIndex, setSelectedLotIndex] = useState(0);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(product);

  // Derived data
  const isLotBased = product?.Lot && product.lotDetails?.length > 0;
  const selectedLot = isLotBased ? product.lotDetails[selectedLotIndex] : null;

  // Guard clause
  if (!product) {
    return (
      <div className="product-details-container">
        <div className="error">Product not found</div>
        <button onClick={() => navigate(-1)} className="back-button">
          ← Back to Catalog
        </button>
      </div>
    );
  }
  const num = (v) => parseFloat(v) || 0;
  const wastage = num(product.wastage) +
    num(product.specificationMC) +
    (product.specification && product.specification !== "Plane"
      ? 0
      : 0);

  const setMcBase = num(product.setMc);
  const setMc = setMcBase;

  const gramMcPerGram = num(product.netGramMc);

  const specMcPerGram = num(product.specificationGramRate);

  const TotalAmount = () => {
    const total = setMc + gramMcPerGram * parseFloat(selectedLot?.avgNetWt || product.lotDetails[0].avgNetWt) + specMcPerGram * parseFloat(selectedLot?.avgSpecWt || product.lotDetails[0].avgSpecWt);
    return total.toFixed(2);
  }
  const calculateFineWeight = () => {
    const netWeight = num(selectedLot?.avgNetWt) || num(product.lotDetails[0].avgNetWt);
    const fineWeight = netWeight * (parseFloat(product.purity) + parseFloat(wastage)) / 100;
    return fineWeight.toFixed(3);
  };

  const displayPurity = product.category?.replace(/(\d+)(HUID|ORNA)/i, "$1 $2");

  // ==========================
  // 🖱️ Event Handlers
  // ==========================
  console.log(product);
  const renderImageGallery = () => {
    const images = Array.isArray(product.images) ? product.images : [];
    const validImages = images.filter(
      (img) => img && typeof img.url === "string" && img.url.trim() !== ""
    );
    const safeIndex = selectedImage >= validImages.length ? 0 : selectedImage;

    return (
      <div className="product-gallery">
        <div className="main-image">
          {validImages.length > 0 ? (
            <img
              src={validImages[safeIndex]?.url?.trim()}
              alt={product.productName}
              onError={(e) => {
                e.target.src = "https://via.placeholder.com/600x600?text=No+Image";
              }}
            />
          ) : (
            <div className="image-placeholder">
              No Image Available
            </div>
          )}
        </div>

        {validImages.length > 0 && (
          <div className="thumbnail-container">
            {validImages.map((image, index) => (
              <div
                key={image.id || index}
                className={`thumbnail ${safeIndex === index ? "active" : ""}`}
                onClick={() => setSelectedImage(index)}
              >
                <img
                  src={image.url?.trim()}
                  alt={`Thumbnail ${index + 1}`}
                  onError={(e) => {
                    e.target.src = "https://via.placeholder.com/100x100?text=No+Image";
                  }}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderVariantInfo = () => {

    return (
      <div>
        <div className="lot-chips">{product.lotDetails.map((lot, index) => (
          <button
            key={index}
            className={`chip ${selectedLotIndex === index ? 'chip-selected' : ''}`}
            onClick={() => setSelectedLotIndex(index)}
          >
            {lot.size || `Lot ${index + 1}`}
          </button>
        ))}</div>
        <div><div className="spec-lot-item">
          <span className="spec-label">Net wt ~ {parseFloat(product.lotDetails[selectedLotIndex].avgNetWt || 0).toFixed(3)} g</span>
          <span className="spec-label">Avl set: {product.lotDetails[selectedLotIndex].set}</span>
        </div></div>
      </div>
    );
  }



  const renderSpecifications = () => {
    // Add safety check for selectedLot
    if (isLotBased && !selectedLot) {
      return <div className="error">Lot details not available</div>;
    }

    return (
      <div className="specifications-card">
        <h2>Specifications</h2>
        <div className="spec-grid">
          <div>
            <SpecRow label="Status" value={product.serviceType === "ready" ? "Ready Serve" : "Order Serve"} />
            {product.designCode && <SpecRow label="Design Code" value={product.designCode} />}
            <SpecRow label="Segment" value={product.segment} />
            <SpecRow label="Category" value={product.category} />
            <SpecRow label="Purity" value={`${parseFloat(product.purity || 0).toFixed(2)}%`} />
            <SpecRow label="Product" value={`${product.productSource} ${product.productName}`} />
            <SpecRow label="Specification" value={product.specification} />
            <SpecRow label="Finish Type" value={product.finishType || product.styleType || "Regular"} />
            <SpecRow label="Settlement" value={product.paymentMethod} />
          </div>
          <div>
            <SpecRow label="Set" value={1} />
            <SpecRow label="Gross Wt." value={isLotBased ? `~${parseFloat(selectedLot?.avgGrossWt || 0).toFixed(3)} g` : `~${parseFloat(product.lotDetails[0].avgGrossWt || 0).toFixed(3)} g`} />
            {product.specification !== "PLANE" ?
              <SpecRow label={`${product.specification || 'Spec'} Wt.`} value={isLotBased ? `~${parseFloat(selectedLot?.avgSpecWt || 0).toFixed(3)} g` : `~${parseFloat(product.lotDetails[0].avgSpecWt || 0).toFixed(3)} g`} /> : ""}
            <SpecRow label="Net Wt." value={isLotBased ? `~${parseFloat(selectedLot?.avgNetWt || 0).toFixed(3)} g` : `~${parseFloat(product.lotDetails[0].avgNetWt || 0).toFixed(3)} g`} />
            <SpecRow label="Wastage" value={`${parseFloat(wastage || 0).toFixed(2)}%`} />
            <SpecRow label="Set Mc." value={`~₹${setMc.toFixed(2)}`} />
            <SpecRow label="Net Gram Mc." value={`~₹${gramMcPerGram.toFixed(2)}/g`} />
            {product.specification !== "PLANE" ?
              <SpecRow label={`${product.specification || 'Spec'} Wt. Per gm`} value={`~₹${specMcPerGram.toFixed(2)}/g`} /> : ""}
          </div>
        </div>

        <div className="rtgs-section">
          <div><strong>Fine</strong><div className="price">~ {calculateFineWeight()} g</div></div>
          <div><strong>Amount</strong><div className="price">~₹{TotalAmount()}</div></div>
        </div>


      </div>
    );
  };

  // ==========================
  // 🧩 JSX
  // ==========================

  return (
    <>
      <PageHeader title={`${product.segment} ${displayPurity} ${product.productName}`} />
      <div className="product-details-container">
        {currentProduct?.status === "rejected" && (
          <div
            style={{
              margin: "12px 16px",
              padding: "14px 16px",
              background: "#fff1f2",
              border: "1.5px solid #fecdd3",
              borderRadius: "12px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "10px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <AlertCircle color="#e11d48" size={22} />
              <div>
                <strong style={{ color: "#9f1239", fontSize: "14px", display: "block" }}>
                  Product Rejected by QC
                </strong>
                <span style={{ color: "#be123c", fontSize: "13px" }}>
                  {currentProduct.rejectionReason || currentProduct.reason || "Please edit and resubmit for approval."}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsEditModalOpen(true)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                background: "#c49b27",
                color: "#fff",
                border: "none",
                padding: "8px 16px",
                borderRadius: "8px",
                fontSize: "13px",
                fontWeight: "700",
                cursor: "pointer",
              }}
            >
              <Edit3 size={15} />
              Edit & Resubmit
            </button>
          </div>
        )}
        <div className="product-content">
          {renderImageGallery()}

          <div className="additional-info">
            <h3>
              {product.serviceType === "ready" ? "Ready Serve" : "Order Serve"}{" "}
              {product.productSource} {product.productName}
            </h3>
            <span className="spec-label">{renderVariantInfo()}</span>
          </div>
        </div>

       
          {renderSpecifications()}
      </div>

      {isEditModalOpen && currentProduct && (
        <EditRejectedProductModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          product={currentProduct}
          sellerProductData={sellerProductData}
          onProductUpdated={(updated) => {
            setCurrentProduct(updated);
            alert("Product updated and resubmitted!");
            navigate(-1);
          }}
          onProductDeleted={() => {
            navigate(-1);
          }}
        />
      )}

      <Footer />
    </>
  );
};

// 💡 Reusable Spec Row Component
const SpecRow = ({ label, value }) => (
  <div className="spec-row">
    <span className="spec-label">{label}</span>
    <span className="spec-value">{value}</span>
  </div>
);

export default ProductDetails;